import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import client, { apiErr } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { hoursToHM } from "@/lib/duration";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, MapPin, Navigation, CalendarDays, ClipboardList, Activity, AlertTriangle, Loader2, Play, CheckCircle2, Info, Pencil, MessageSquare, Clock, Camera } from "lucide-react";
import { toast } from "sonner";
import { FIELD_JOB_TYPES, jobTypeLabel } from "@/lib/fieldTypes";
import { WorkerMultiSelect } from "@/components/WorkerMultiSelect";
import { StaffingBadge } from "@/components/StaffingBadge";
import PropertyThread from "@/components/PropertyThread";
import { FilePhoto } from "@/components/FilePhoto";
import { PropertyFrontPhoto } from "@/components/PropertyFrontPhoto";
import { MapLink } from "@/components/MapLink";
import { JobInfoCard, JobNotesLog } from "@/components/JobNotes";
import { JobChecklists } from "@/components/forms/JobChecklists";
import { RequiredPhotos } from "@/components/RequiredPhotos";
import { Link } from "react-router-dom";

const STAT = { scheduled: "bg-emerald-50 text-emerald-700 border-emerald-200", active: "bg-amber-50 text-amber-700 border-amber-200", completed: "bg-slate-100 text-slate-600", cancelled: "bg-red-50 text-red-700 border-red-200" };

function Section({ icon: Icon, title, children, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
      <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-3"><Icon className="h-3.5 w-3.5 text-brand" />{title}</p>
      {children}
    </div>
  );
}
const Field = ({ label, value }) => value ? (
  <div className="flex justify-between gap-4 py-1 text-sm border-b border-slate-50 last:border-0">
    <span className="text-slate-500 shrink-0">{label}</span><span className="text-slate-800 text-right break-words">{value}</span>
  </div>
) : null;

const PHOTO_GROUPS = [["arrival", "Arrival"], ["before", "Before"], ["after", "After"], ["completion", "Completion"], ["job", "Job photos"]];

function PhotoGroups({ photos }) {
  const { user } = useAuth();
  const staffMeta = ["owner", "admin", "office", "supervisor"].includes(user?.role);
  return PHOTO_GROUPS.map(([key, label]) => {
    const list = photos.filter((p) => (p.type || "job") === key);
    if (!list.length) return null;
    return (
      <div key={key} className="pt-1" data-testid={`jp-photo-group-${key}`}>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">{label}</p>
        <div className="flex flex-wrap gap-2">
          {list.map((p) => (
            <div key={p.fileId} className="text-center">
              <FilePhoto id={p.fileId} />
              {staffMeta && p.at && <p className="text-[10px] text-slate-400" data-testid={`jp-photo-meta-${p.fileId}`}>{new Date(p.at).toLocaleString("en-GB", { timeZone: "Europe/London" })}</p>}
            </div>
          ))}
        </div>
      </div>
    );
  });
}

function JobNotesPhotos({ data }) {
  if (!data.notes?.length && !data.photos?.length && !data.visits.some((v) => v.notes)) return (
    <Section icon={MessageSquare} title="Notes & photos" testid="jp-section-notes"><p className="text-sm text-slate-400">No notes or photos yet. Cleaners' notes and photos from every visit will appear here.</p></Section>
  );
  const byDate = new Map();
  const slot = (d) => { const k = d || "—"; if (!byDate.has(k)) byDate.set(k, { notes: [], jobNotes: [], photos: [] }); return byDate.get(k); };
  data.notes.forEach((n) => slot(n.date).notes.push(n));
  data.visits.filter((v) => v.notes).forEach((v) => slot(v.date).jobNotes.push(v));
  data.photos.forEach((p) => slot(p.date).photos.push(p));
  const dates = [...byDate.keys()].sort((a, b) => (a === "—" ? 1 : b === "—" ? -1 : b.localeCompare(a)));
  const fmtDay = (d) => (d === "—" ? "Other" : new Date(`${d}T12:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" }));
  return (
    <Section icon={MessageSquare} title="Notes & photos by visit" testid="jp-section-notes">
      <div className="space-y-4">
        {dates.map((d) => {
          const g = byDate.get(d);
          return (
            <div key={d} className="rounded-lg border border-slate-200 p-3" data-testid={`jp-visit-notes-${d}`}>
              <p className="text-sm font-semibold text-slate-900 mb-2">Visit · {fmtDay(d)}</p>
              <div className="space-y-2">
                {g.notes.map((n, i) => (
                  <div key={`n${i}`} className="text-sm border-l-2 border-brand/40 pl-2" data-testid={`jp-note-${d}-${i}`}>
                    <p className="text-slate-800 whitespace-pre-wrap">{n.text}</p>
                    <p className="text-xs text-slate-400">{n.by}</p>
                  </div>
                ))}
                {g.jobNotes.map((v) => (
                  <div key={`v${v.id}`} className="text-sm border-l-2 border-slate-200 pl-2">
                    <p className="text-slate-700 whitespace-pre-wrap">{v.notes}</p><p className="text-xs text-slate-400">Job note</p>
                  </div>
                ))}
                {g.photos.length > 0 && <PhotoGroups photos={g.photos} />}
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}


function VisitRow({ v, currentId, onOpen }) {
  return (
    <button type="button" onClick={() => onOpen(v.id)} data-testid={`jp-visit-${v.id}`}
      className={`w-full flex flex-wrap items-center gap-x-4 gap-y-1 py-2 px-1 text-left text-sm hover:bg-slate-50 ${v.id === currentId ? "bg-brand/5" : ""}`}>
      <span className="font-medium text-slate-800 whitespace-nowrap">{v.date}</span>
      <span className="text-slate-500 whitespace-nowrap">{v.startTime}–{v.endTime}</span>
      <span className="flex-1 min-w-[120px] text-slate-600 truncate">{v.workers.join(", ") || "Unassigned"}</span>
      <Badge variant="outline" className={`${STAT[v.status] || ""} text-xs`}>{v.status}</Badge>
      <span className="ml-auto whitespace-nowrap text-right font-semibold text-slate-800" data-testid={`jp-visit-hours-${v.id}`}>{hoursToHM(v.hours)}</span>
    </button>
  );
}

function JobVisits({ data, currentId, onOpen }) {
  const today = new Date().toLocaleDateString("en-CA", { timeZone: "Europe/London" });
  const upcoming = data.visits.filter((v) => (v.date || "") >= today && v.status !== "completed");
  const past = data.visits.filter((v) => !upcoming.includes(v)).reverse();
  const block = (label, list, tid) => (
    <div data-testid={tid}>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 pt-2 pb-1">{label} ({list.length})</p>
      {list.length ? <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">{list.map((v) => <VisitRow key={v.id} v={v} currentId={currentId} onOpen={onOpen} />)}</div>
        : <p className="text-sm text-slate-400 pb-2">None</p>}
    </div>
  );
  return (
    <Section icon={ClipboardList} title={`All visits under #${data.jobNumber ?? "—"} · ${data.count} visit${data.count === 1 ? "" : "s"}`} testid="jp-section-visits">
      {block("Upcoming", upcoming, "jp-visits-upcoming")}
      {block("Past", past, "jp-visits-past")}
    </Section>
  );
}

const schedMins = (a, b) => {
  if (!a || !b) return null;
  const m = (s) => Number(s.slice(0, 2)) * 60 + Number(s.slice(3, 5));
  let d = m(b) - m(a); if (d < 0) d += 1440;
  return d;
};

const lonHM = (ts) => (ts ? new Date(ts).toLocaleTimeString("en-GB", { timeZone: "Europe/London", hour: "2-digit", minute: "2-digit" }) : "");
const lonISO = (day, hhmm) => {
  const guess = new Date(`${day}T${hhmm}:00Z`);
  const lon = new Date(guess.toLocaleString("en-US", { timeZone: "Europe/London" }));
  const utc = new Date(guess.toLocaleString("en-US", { timeZone: "UTC" }));
  return new Date(guess.getTime() - (lon - utc)).toISOString();
};

function TimeCell({ ts, day, eventId, open, editable, onSaved, testid }) {
  const [val, setVal] = useState(lonHM(ts));
  useEffect(() => { setVal(lonHM(ts)); }, [ts]);
  if (!eventId) return open
    ? <span className="text-amber-600 font-medium" data-testid={testid}>Running now</span>
    : <span className="text-red-600 font-medium" data-testid={testid}>No finish recorded</span>;
  if (!editable) return <span className="font-medium text-slate-800" data-testid={testid}>{val}</span>;
  const save = async () => {
    if (!val || val === lonHM(ts)) return;
    try { await client.patch(`/attendance/job-event/${eventId}`, { timestamp: lonISO(day, val), reason: "Edited in job profile" }); toast.success("Time updated"); onSaved(); }
    catch (er) { toast.error(apiErr(er.response?.data?.detail)); setVal(lonHM(ts)); }
  };
  return <Input type="time" value={val} onChange={(e) => setVal(e.target.value)} onBlur={save} className="h-8 w-[130px]" data-testid={testid} />;
}

function VisitTimes({ visits, currentId, onSaved }) {
  const { user } = useAuth();
  const editable = ["owner", "admin"].includes(user?.role);
  const list = (visits?.visits || []).filter((v) => v.segments?.length).slice().reverse();
  if (!list.length) return <p className="mt-4 text-sm text-slate-400" data-testid="jp-times-empty">No start or finish times recorded yet.</p>;
  return (
    <div className="mt-4" data-testid="jp-visit-times">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 pb-1">Start &amp; finish times{editable ? " · tap a time to edit" : ""}</p>
      <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
        {list.flatMap((v) => v.segments.map((s) => (
          <div key={s.startEventId} className={`flex flex-wrap items-center gap-x-3 gap-y-1 py-2 px-1 text-sm ${v.id === currentId ? "bg-brand/5" : ""}`} data-testid={`jp-seg-${s.startEventId}`}>
            <span className="w-24 font-medium text-slate-800">{v.date}</span>
            <span className="flex-1 min-w-[100px] text-slate-600 truncate">{s.worker}</span>
            <TimeCell ts={s.start} day={v.date} eventId={s.startEventId} editable={editable} onSaved={onSaved} testid={`jp-seg-start-${s.startEventId}`} />
            <span className="text-slate-400">–</span>
            <TimeCell ts={s.end} day={v.date} eventId={s.endEventId} open={s.open} editable={editable} onSaved={onSaved} testid={`jp-seg-end-${s.startEventId}`} />
            <span className="w-14 text-right font-semibold text-slate-800" data-testid={`jp-seg-hours-${s.startEventId}`}>{s.hours != null ? hoursToHM(s.hours) : "—"}</span>
          </div>
        )))}
      </div>
    </div>
  );
}

function HoursWorked({ visits, currentId, onSaved }) {
  const cur = visits?.visits?.find((v) => v.id === currentId);
  const box = (label, value, tid, accent) => (
    <div className={`rounded-xl border p-4 ${accent}`} data-testid={tid}>
      <p className="text-xs font-semibold uppercase tracking-wide opacity-70">{label}</p>
      <p className="mt-1 text-2xl font-bold">{value}</p>
    </div>
  );
  return (
    <Section icon={Clock} title="Hours worked" testid="jp-section-hours">
      <div className="grid grid-cols-2 gap-3">
        {box("This visit", hoursToHM(cur?.hours || 0), "jp-hours-visit", "border-emerald-200 bg-emerald-50 text-emerald-800")}
        {box("All visits", hoursToHM(visits?.totalHours || 0), "jp-hours-total", "border-slate-200 bg-slate-50 text-slate-800")}
      </div>
      <VisitTimes visits={visits} currentId={currentId} onSaved={onSaved} />
    </Section>
  );
}

export default function JobProfile({ jobId, embedded = false }) {
  const params = useParams();
  const [embeddedId, setEmbeddedId] = useState(jobId);
  useEffect(() => { setEmbeddedId(jobId); }, [jobId]);
  const id = embedded ? embeddedId : params.id;
  const nav = useNavigate();
  const { t } = useI18n();
  const { can, messagingEnabled } = useAuth();
  const canManage = can("job.manage");
  const [job, setJob] = useState(null);
  const [clients, setClients] = useState([]);
  const [properties, setProperties] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [issues, setIssues] = useState([]);
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [visits, setVisits] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await client.get(`/jobs/${id}`);
      setJob(data);
      setEdit({
        clientId: data.clientId || "", propertyId: data.propertyId || "", workerIds: data.workerIds || [],
        requiredWorkerCount: data.requiredWorkerCount || 1, date: data.date || "", startTime: data.startTime || "09:00",
        endTime: data.endTime || "17:00", jobType: data.jobType || "one_off", jobTypeOther: data.jobTypeOther || "",
        status: data.status || "scheduled", instructions: data.instructions || "", accessInstructions: data.accessInstructions || "",
        geofenceEnabled: data.geofenceEnabled ?? false, geofenceRadius: data.geofenceRadius ?? "",
      });
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    client.get(`/jobs/${id}/activity`).then((r) => setActivity(r.data.items || [])).catch(() => {});
    client.get(`/issues?jobId=${id}`).then((r) => setIssues(r.data)).catch(() => {});
    client.get(`/jobs/${id}/visits`).then((r) => setVisits(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    refresh();
    client.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    client.get("/properties").then((r) => setProperties(r.data)).catch(() => {});
    client.get("/users").then((r) => setWorkers(r.data.filter((u) => u.role === "cleaner"))).catch(() => {});
  }, [refresh]);

  if (!job || !edit) return <div className="p-8 text-slate-400" data-testid="job-profile-loading">Loading…</div>;

  const g = job.propertyGeo;
  const propsForClient = properties.filter((p) => !edit.clientId || p.clientId === edit.clientId);
  const directions = () => {
    const dest = g?.lat != null ? `${g.lat},${g.lng}` : encodeURIComponent(job.propertyAddress || "");
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${dest}`, "_blank");
  };
  const act = async (fn) => { setBusy(true); try { await fn(); toast.success(t("cal.saved", "Updated")); await refresh(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); } };
  const jobPayload = (extra = {}) => ({ ...edit, geofenceRadius: edit.geofenceRadius === "" || edit.geofenceRadius == null ? null : Number(edit.geofenceRadius), ...extra });
  const saveEdit = () => act(async () => { await client.patch(`/jobs/${id}`, jobPayload()); setEditing(false); });
  const setStatus = (status) => act(async () => { await client.patch(`/jobs/${id}`, jobPayload({ status })); });
  const addIssue = () => act(async () => { const title = window.prompt(t("jd.issueTitle", "Issue title")); if (!title) return; await client.post("/issues", { jobId: id, title, priority: "medium" }); });


  return (
    <div className={embedded ? "space-y-5 min-w-0 w-full overflow-x-hidden" : "space-y-5"} data-testid={embedded ? "job-profile-window" : "job-profile-page"}>
      {!embedded && (
        <button onClick={() => nav("/jobs")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-brand" data-testid="job-back">
          <ArrowLeft className="h-4 w-4" />{t("nav.jobs", "Jobs")}
        </button>
      )}

      <Card className="p-6 border-slate-200">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <Badge className="bg-brand">{t("field.label", "iClean Field")}</Badge>
              <Badge variant="outline" className="border-brand/40 text-brand">{jobTypeLabel(t, job.jobType)}{job.jobType === "other" && job.jobTypeOther ? ` · ${job.jobTypeOther}` : ""}</Badge>
              <Badge variant="outline" className={STAT[job.status] || ""}>{t(`st.${job.status}`, job.status)}</Badge>
            </div>
            {job.propertyFrontPhotoId && <div className="my-2"><PropertyFrontPhoto fileId={job.propertyFrontPhotoId} testid="jp-front-photo" /></div>}
            <h2 className="font-display text-2xl font-bold text-brand-ink" data-testid="jp-client-name">
              {can("client.view") && job.client?.id ? <Link to={`/clients/${job.client.id}`} className="hover:underline" data-testid="jp-client-link">{job.clientName}</Link> : job.clientName}
            </h2>
            {job.client?.email && <a href={`mailto:${job.client.email}`} className="text-sm text-brand hover:underline" data-testid="jp-client-email">{job.client.email}</a>}
            <p className="text-sm text-slate-500 flex items-center gap-3 flex-wrap mt-0.5">
              <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /><MapLink address={job.propertyAddress} geo={job.propertyGeo} testid="jp-address-link" /></span>
              <span className="inline-flex items-center gap-1 text-slate-700" data-testid="jp-schedule"><CalendarDays className="h-3.5 w-3.5" />{job.date} · {job.startTime}–{job.endTime}{schedMins(job.startTime, job.endTime) != null ? ` (${hoursToHM(schedMins(job.startTime, job.endTime) / 60)} scheduled)` : ""}</span>
              <span data-testid="jp-job-number">#{job.jobNumber}</span>
              <StaffingBadge assigned={job.workers?.length || 0} required={job.requiredWorkerCount} testid="jp-staffing" />
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="jp-actions">
            <Button size="sm" variant="outline" data-testid="jp-directions" onClick={directions}><Navigation className="h-4 w-4 mr-1.5" />{t("job.directions", "Get directions")}</Button>
            {canManage && !editing && <Button size="sm" variant="outline" data-testid="jp-edit" onClick={() => setEditing(true)}><Pencil className="h-4 w-4 mr-1.5" />{t("common.edit", "Edit")}</Button>}
            {can("issue.view") && <Button size="sm" variant="outline" data-testid="jp-add-issue" onClick={addIssue}><AlertTriangle className="h-4 w-4 mr-1.5" />{t("jd.addIssue", "Add issue")}</Button>}
            {canManage && job.status !== "active" && <Button size="sm" data-testid="jp-start" className="bg-teal-600 hover:bg-teal-700" onClick={() => setStatus("active")} disabled={busy}><Play className="h-4 w-4 mr-1.5" />{t("jd.startJob", "Start job")}</Button>}
            {canManage && job.status === "active" && <Button size="sm" data-testid="jp-complete" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setStatus("completed")} disabled={busy}><CheckCircle2 className="h-4 w-4 mr-1.5" />{t("jd.completeJob", "Complete job")}</Button>}
          </div>
        </div>
      </Card>

      <Tabs defaultValue="details">
        <TabsList className="max-w-full overflow-x-auto justify-start">
          <TabsTrigger value="details" data-testid="jp-tab-details"><Info className="h-4 w-4 mr-1.5" />{t("jd.details", "Details")}</TabsTrigger>
          <TabsTrigger value="notes" data-testid="jp-tab-notes"><Camera className="h-4 w-4 mr-1.5" />Notes &amp; photos</TabsTrigger>
          <TabsTrigger value="labour" data-testid="jp-tab-labour"><Clock className="h-4 w-4 mr-1.5" />Labour</TabsTrigger>
          <TabsTrigger value="forms" data-testid="jp-tab-forms"><ClipboardList className="h-4 w-4 mr-1.5" />{t("form.forms", "Forms")}</TabsTrigger>
          <TabsTrigger value="activity" data-testid="jp-tab-activity"><Activity className="h-4 w-4 mr-1.5" />{t("jd.activity", "Activity")}</TabsTrigger>
          {messagingEnabled && <TabsTrigger value="messages" data-testid="jp-tab-messages"><MessageSquare className="h-4 w-4 mr-1.5" />{t("jd.messages", "Messages")}</TabsTrigger>}
        </TabsList>

        <TabsContent value="details" className="pt-4">
          {editing && canManage ? (
            <Card className="p-5 border-slate-200 max-w-2xl space-y-4" data-testid="jp-edit-card">
              <div>
                <Label>{t("job.type", "Job type")}</Label>
                <Select value={edit.jobType} onValueChange={(v) => setEdit({ ...edit, jobType: v })}>
                  <SelectTrigger data-testid="jp-type-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{FIELD_JOB_TYPES.map((k) => <SelectItem key={k} value={k}>{t(`jt.${k}`, k)}</SelectItem>)}</SelectContent>
                </Select>
                {edit.jobType === "other" && <Input data-testid="jp-type-other" value={edit.jobTypeOther} onChange={(e) => setEdit({ ...edit, jobTypeOther: e.target.value })} placeholder={t("job.typeOther", "Describe (other)")} className="mt-2" />}
              </div>
              <div>
                <Label>{t("job.client", "Client")}</Label>
                <Select value={edit.clientId} onValueChange={(v) => setEdit({ ...edit, clientId: v, propertyId: "" })}>
                  <SelectTrigger data-testid="jp-client-select" className="mt-1.5"><SelectValue placeholder={t("job.client", "Client")} /></SelectTrigger>
                  <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("job.property", "Property")}</Label>
                <Select value={edit.propertyId} onValueChange={(v) => setEdit({ ...edit, propertyId: v })}>
                  <SelectTrigger data-testid="jp-property-select" className="mt-1.5"><SelectValue placeholder={t("job.property", "Property")} /></SelectTrigger>
                  <SelectContent>{propsForClient.map((p) => <SelectItem key={p.id} value={p.id}>{p.address}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("job.workers", "Workers")}</Label>
                <WorkerMultiSelect workers={workers} value={edit.workerIds} onChange={(v) => setEdit({ ...edit, workerIds: v })} testid="jp-workers-multi" />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs">{t("stf.required", "Required workers")}</Label>
                <Input type="number" min="1" data-testid="jp-required" value={edit.requiredWorkerCount} onChange={(e) => setEdit({ ...edit, requiredWorkerCount: Math.max(1, Number(e.target.value) || 1) })} className="w-24 h-9" />
                <StaffingBadge assigned={edit.workerIds.length} required={edit.requiredWorkerCount} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>{t("job.date", "Date")}</Label><Input type="date" data-testid="jp-date" value={edit.date} onChange={(e) => setEdit({ ...edit, date: e.target.value })} className="mt-1.5" /></div>
                <div><Label>{t("sched.start", "Start")}</Label><Input type="time" data-testid="jp-start-time" value={edit.startTime} onChange={(e) => setEdit({ ...edit, startTime: e.target.value })} className="mt-1.5" /></div>
                <div><Label>{t("sched.end", "End")}</Label><Input type="time" data-testid="jp-end-time" value={edit.endTime} onChange={(e) => setEdit({ ...edit, endTime: e.target.value })} className="mt-1.5" /></div>
              </div>
              <div>
                <Label>{t("common.status", "Status")}</Label>
                <Select value={edit.status} onValueChange={(v) => setEdit({ ...edit, status: v })}>
                  <SelectTrigger data-testid="jp-status-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{["scheduled", "active", "completed", "cancelled"].map((s) => <SelectItem key={s} value={s}>{t(`st.${s}`, s)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>{t("job.accessInstructions", "Job access instructions")}</Label><Textarea data-testid="jp-access-instructions" value={edit.accessInstructions} onChange={(e) => setEdit({ ...edit, accessInstructions: e.target.value })} className="mt-1.5" /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
                <label className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5">
                  <span className="text-sm text-slate-700">{t("jd.geofenceEnforce", "Enforce geofence on this job")}</span>
                  <Switch data-testid="jp-geofence-toggle" checked={!!edit.geofenceEnabled} onCheckedChange={(v) => setEdit({ ...edit, geofenceEnabled: v })} />
                </label>
                <div><Label>{t("jd.geofenceRadius", "Radius (m) — blank inherits property")}</Label><Input type="number" data-testid="jp-geofence-radius" value={edit.geofenceRadius} onChange={(e) => setEdit({ ...edit, geofenceRadius: e.target.value })} className="mt-1.5" /></div>
              </div>
              <div><Label>{t("prop.instructions", "Instructions")}</Label><Textarea data-testid="jp-instructions" value={edit.instructions} onChange={(e) => setEdit({ ...edit, instructions: e.target.value })} className="mt-1.5" /></div>
              <div className="flex gap-2 justify-end">
                <Button variant="ghost" onClick={() => { setEditing(false); refresh(); }}>{t("common.cancel", "Cancel")}</Button>
                <Button data-testid="jp-save" onClick={saveEdit} disabled={busy || !edit.clientId || !edit.propertyId || !edit.date} className="bg-brand hover:bg-brand-dark">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.save", "Save")}</Button>
              </div>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 gap-3">
              <Section icon={Info} title={t("jd.details", "Job details")} testid="jp-section-details">
                <Field label={t("job.type", "Job type")} value={jobTypeLabel(t, job.jobType)} />
                <Field label={t("common.status", "Status")} value={t(`st.${job.status}`, job.status)} />
                <Field label={t("cal.instructions", "Instructions")} value={job.instructions} />
                <Field label={t("job.accessInstructions", "Job access instructions")} value={job.accessInstructions} />
              </Section>
              <JobInfoCard jobId={id} info={job.jobInfo} canEdit={canManage} onSaved={refresh} />
              <div className="md:col-span-2"><JobNotesLog jobId={id} /></div>
              {visits && (
                <div className="md:col-span-2"><JobVisits data={visits} currentId={id} onOpen={(v) => (embedded ? setEmbeddedId(v) : nav(`/jobs/${v}`))} /></div>
              )}
              <div className="md:col-span-2"><RequiredPhotos job={job} canManage={canManage} onSaved={refresh} /></div>
              {issues.length > 0 && (
                <Section icon={AlertTriangle} title={t("jd.issues", "Issues")} testid="jp-section-issues">
                  <div className="space-y-1.5">
                    {issues.map((i) => <div key={i.id} className="flex items-center justify-between text-sm"><span className="text-slate-800">{i.title}</span><Badge variant="outline" className="text-xs">{t(`status.${i.status}`, i.status)}</Badge></div>)}
                  </div>
                </Section>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="notes" className="pt-4">
          {visits ? <JobNotesPhotos data={visits} /> : <Loader2 className="h-5 w-5 animate-spin text-slate-400" />}
        </TabsContent>

        <TabsContent value="labour" className="pt-4">
          <HoursWorked visits={visits} currentId={id} onSaved={refresh} />
        </TabsContent>

        <TabsContent value="forms" className="pt-4">
          <JobChecklists jobId={id} isSeries={!!job.seriesId} canManage={can("checklist.manage")} />
        </TabsContent>

        <TabsContent value="activity" className="pt-4">
          <Card className="p-5 border-slate-200">
            {activity.length === 0 ? <p className="text-sm text-slate-400">{t("jd.noActivity", "No activity yet.")}</p> : (
              <div className="space-y-2">
                {activity.slice(0, 40).map((a, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-sm">
                    <span className="mt-1 h-1.5 w-1.5 rounded-full bg-brand shrink-0" />
                    <div>
                      <span className="text-slate-800">{a.who ? `${a.who} ` : ""}{t(`jd.act.${a.type}`, a.type)}{a.meta?.name ? ` · ${a.meta.name}` : ""}{a.meta?.title ? ` · ${a.meta.title}` : ""}{a.meta?.hours != null ? ` · ${hoursToHM(a.meta.hours)}` : ""}</span>
                      <div className="text-xs text-slate-400">{new Date(a.at).toLocaleString()}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="messages" className="pt-4">
          <PropertyThread propertyId={job.propertyId} title={job.propertyAddress} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
